import type { FunctionComponent } from 'react';
import { useCallback } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import { Badge } from '~/ui/core/badge/Badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconPeopleAdd } from '~/ui/core/icons';

import { createAssignReviewerItem, useAssignReviewer } from '../../api/use-assign-reviewer';
import type { JobItemDetails } from '../../api/use-job-item-details';
import { GenericReviewerContextMenuComponent } from '../../components/ReviewerComponents';
import { useTrackWorkflowTransition } from '../../hooks/analytics/use-track-workflow-transition';
import { PromptShell } from './PromptShell';
import { ReviewerLeading } from './ReviewerLeading';

type ItemType = 'job-level' | 'job-role';

export const AssignReviewerPrompt: FunctionComponent<{
    readonly architectureId: string;
    readonly item: JobItemDetails;
    readonly itemType: ItemType;
    readonly jobType: string;
}> = ({ architectureId, item, itemType, jobType }) => {
    const { applicationState } = useApplicationContext();
    const translate = useTranslate();
    const { mutateAsync: assignReviewer } = useAssignReviewer();
    const { trackWorkflowTransition } = useTrackWorkflowTransition({ architectureId });

    const handleAssignMyself = useCallback(async (): Promise<void> => {
        const id = applicationState.myUserProfile.get('id').unwrapOr('');
        const firstName = applicationState.myUserProfile.get('firstName').unwrapOr('');
        const lastName = applicationState.myUserProfile.get('lastName').unwrapOr('');
        const clusterId = item.parentId;

        if (id.length === 0 || clusterId == null) {
            return;
        }

        await assignReviewer({
            clusterId,
            item: createAssignReviewerItem(item, { id, firstName, lastName, state: 'assigned' }, item.type),
            updateType: item.reviewer === undefined ? 'create' : 'update',
        });

        trackWorkflowTransition({
            actionType: 'assign_reviewer',
            itemId: item.id,
            itemType,
            parentRoleId: itemType === 'job-level' && item.parentId != null ? item.parentId : undefined,
            targetUserId: id,
            toStatus: 'in_review',
            versionId: item.versionInfo?.version.toString() ?? '1',
        });
    }, [applicationState.myUserProfile, assignReviewer, item, itemType, trackWorkflowTransition]);

    return (
        <PromptShell
            question={translate('pages.job-architecture-v2.prompts.assign-reviewer-question', { jobType })}
            leading={<ReviewerLeading item={item} />}
            contextLine={translate('pages.job-architecture-v2.prompts.not-assigned')}
            actions={
                <>
                    <Badge variant="gray" onClick={() => void handleAssignMyself()}>
                        {translate('pages.job-architecture-v2.prompts.assign-myself')}
                    </Badge>
                    <DropdownMenu>
                        <DropdownMenuTrigger disabled={item.actions?.canAssignReviewer !== true}>
                            <Badge
                                variant="warning"
                                onClick={() => {
                                    /* noop — DropdownMenuTrigger handles the open */
                                }}
                            >
                                <IconPeopleAdd size={11} />
                                {translate('pages.job-architecture-v2.job-add-reviewer')}
                            </Badge>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" collisionPadding={16} side="bottom" style={{ zIndex: 3000 }}>
                            <GenericReviewerContextMenuComponent
                                item={item}
                                itemType={itemType}
                                jobArchitectureId={architectureId}
                                jobClusterId={item.parentId ?? undefined}
                                onlyAssignReviewer={true}
                            />
                        </DropdownMenuContent>
                    </DropdownMenu>
                </>
            }
        />
    );
};
